import type React from 'react';
import type { CSSProperties } from 'react';
import { useWordsHappenTheme } from '../../theme';
import styles from './challenge.module.scss';

export interface ChallengeProps {
  title: string;
  subtitle?: string;
  reward: string;
  progress: number;
  total: number;
  badge?: 'new' | 'bonus' | 'weekly';
  done?: boolean;
  // TODO: implement locked state
  locked?: boolean;
  icon: React.ReactNode;
  className?: string;
}

const BADGE_CLASS: Record<NonNullable<ChallengeProps['badge']>, string> = {
  new: styles.badgeNew,
  bonus: styles.badgeBonus,
  weekly: styles.badgeWeekly,
};

const CheckIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 8l3.5 3.5L13 5" stroke="var(--wh-teal)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const Challenge: React.FC<ChallengeProps> = ({
  title,
  subtitle,
  reward,
  progress,
  total,
  badge,
  done = false,
  icon,
  className,
}) => {
  const { theme } = useWordsHappenTheme();
  const isMeadow = theme === 'meadow';

  const clampedProgress = Math.min(Math.max(progress, 0), total);
  const fillPercent = total > 0 ? (clampedProgress / total) * 100 : 0;
  const displayProgress = done ? total : clampedProgress;
  const displayFill = done ? 100 : fillPercent;

  const ariaLabel = done ? `${title}, completed` : title;

  const cls = [styles.challenge, isMeadow ? styles.meadow : styles.flat, done ? styles.done : null, className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cls} aria-label={ariaLabel}>
      <div className={styles.icon} aria-hidden="true">
        {icon}
      </div>

      <div className={styles.body}>
        {badge && (
          <span className={`${styles.badge} ${BADGE_CLASS[badge]}`} aria-hidden="true">
            {badge}
          </span>
        )}

        <span className={styles.title}>{title}</span>
        {subtitle && <span className={styles.subtitle}>{subtitle}</span>}

        <div
          className={styles.progressBar}
          role="progressbar"
          aria-valuenow={displayProgress}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-label="Challenge progress"
        >
          <div className={styles.progressFill} style={{ '--fill-width': `${displayFill}%` } as CSSProperties} />
        </div>

        <div className={styles.footer}>
          <span className={styles.reward}>{reward}</span>
          {done ? (
            <span className={styles.progressDone}>
              <CheckIcon />
            </span>
          ) : (
            <span className={styles.progressLabel}>
              {clampedProgress}/{total}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
